import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { applicationByUser } from "../lib/applications";
import { UploadedRow } from "./RegisterArchitect";
import RegisterArchitect from "./RegisterArchitect";
import { Toaster } from "sonner";
import {
  BadgeCheck,
  ChevronRight,
  Hourglass,
  RefreshCcw,
} from "lucide-react";

/**
 * /architect/application — the only screen an unapproved architect sees.
 * Displays the right content for each state (pending, more_info, approved,
 * rejected). In more_info, "Update & resubmit" swaps the page into resubmit
 * mode of the RegisterArchitect form.
 */
export default function ArchitectApplication() {
  const { user } = useRole();
  const [app, setApp] = useState(null);
  const [err, setErr] = useState(null);
  const [resubmitMode, setResubmitMode] = useState(false);

  async function reload() {
    if (!user?.id) return;
    try {
      const data = await applicationByUser(user.id);
      setApp(data);
    } catch (e) {
      setErr(e);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (err) {
    return <NotFoundPanel />;
  }

  if (!app) {
    return (
      <section
        className="animate-fade-in-up"
        data-testid="architect-application-page"
      >
        <div style={{ color: "#6C6B85", fontStyle: "italic" }}>
          Loading your application…
        </div>
      </section>
    );
  }

  if (resubmitMode) {
    return (
      <RegisterArchitect
        mode="resubmit"
        initial={app}
        applicationId={app.id}
        actorUserId={user.id}
        reviewNote={app.review_notes}
      />
    );
  }

  return (
    <section
      className="animate-fade-in-up"
      data-testid="architect-application-page"
    >
      <Toaster position="top-right" richColors closeButton />
      {app.state === "pending_verification" && (
        <PendingPanel app={app} />
      )}
      {app.state === "more_info_requested" && (
        <MoreInfoPanel app={app} onStartResubmit={() => setResubmitMode(true)} />
      )}
      {app.state === "approved" && <ApprovedPanel app={app} />}
      {app.state === "rejected" && <RejectedPanel app={app} />}
    </section>
  );
}

function PageHeader({ eyebrow, title, accentColor = "#B14E2F" }) {
  return (
    <div className="mb-7">
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 28,
            height: 2,
            background: accentColor,
          }}
        />
        <span
          className="wn-label"
          style={{ color: accentColor === "#B14E2F" ? "#8F3C23" : accentColor }}
        >
          {eyebrow}
        </span>
      </div>
      <h1
        className="mt-4"
        style={{
          fontSize: "clamp(28px, 4vw, 36px)",
          fontWeight: 600,
          lineHeight: 1.1,
        }}
      >
        {title}
      </h1>
    </div>
  );
}

function NotFoundPanel() {
  const nav = useNavigate();
  return (
    <section data-testid="architect-no-application">
      <PageHeader eyebrow="Application status" title="No application on file yet." />
      <p
        className="max-w-[560px]"
        style={{ color: "#6C6B85", fontSize: 15, lineHeight: 1.6 }}
      >
        You're signed in as an architect but don't have an application yet.
        Start one to join the verified network.
      </p>
      <button
        className="wn-btn wn-btn-primary mt-6"
        onClick={() => nav("/register/architect")}
        data-testid="start-application-btn"
      >
        Start an application <ChevronRight size={16} />
      </button>
    </section>
  );
}

// ---------------------------------------------------------------------------
function PendingPanel({ app }) {
  return (
    <div data-testid="status-pending">
      <PageHeader
        eyebrow="Application status"
        title="We're reviewing your application."
      />
      <p
        className="max-w-[640px]"
        style={{ color: "#6C6B85", fontSize: 16, lineHeight: 1.65 }}
      >
        Your application is with the Platform Admin. We will notify you when
        there is a decision.
      </p>
      <div className="mt-6 inline-flex items-center gap-2">
        <Hourglass size={16} style={{ color: "#3A5563" }} />
        <span className="wn-pill wn-pill-info">Pending verification</span>
      </div>
      <SubmissionSummary app={app} />
    </div>
  );
}

function MoreInfoPanel({ app, onStartResubmit }) {
  return (
    <div data-testid="status-more-info">
      <PageHeader
        eyebrow="Application status"
        title="We need a little more from you."
        accentColor="#B98A3C"
      />
      <div
        className="wn-card mt-2"
        style={{
          padding: "16px 18px",
          borderLeft: "3px solid #B98A3C",
          backgroundColor: "rgba(185,138,60,0.07)",
        }}
        data-testid="more-info-note"
      >
        <div className="wn-label" style={{ color: "#8a651f" }}>
          Reviewer note
        </div>
        <p
          style={{
            marginTop: 6,
            color: "#272636",
            fontSize: 14,
            lineHeight: 1.6,
          }}
        >
          {app.review_notes || "Please update your application."}
        </p>
      </div>
      <div className="mt-6">
        <button
          className="wn-btn wn-btn-primary"
          onClick={onStartResubmit}
          data-testid="start-resubmit-btn"
        >
          <RefreshCcw size={16} />
          Update &amp; resubmit
        </button>
      </div>
      <SubmissionSummary app={app} />
    </div>
  );
}

function ApprovedPanel({ app }) {
  const nav = useNavigate();
  const archId = app.architect_id;
  return (
    <div data-testid="status-approved">
      <PageHeader
        eyebrow="Welcome"
        title="Welcome to the network."
        accentColor="#4F6149"
      />
      <div
        className="wn-card"
        style={{
          padding: "20px 22px",
          borderLeft: "3px solid #4F6149",
          backgroundColor: "rgba(79,97,73,0.06)",
        }}
      >
        <div className="flex items-center gap-3">
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              backgroundColor: "#B14E2F",
              color: "#FBFAFD",
              padding: "5px 12px",
              borderRadius: 20,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.04em",
            }}
            data-testid="verified-badge"
          >
            <BadgeCheck size={12} />
            Verified Network Architect
          </span>
        </div>
        <p
          className="mt-4"
          style={{ color: "#272636", fontSize: 15, lineHeight: 1.6 }}
        >
          Your public profile is now live across the platform. Other roles can
          see your portfolio, send you consultations, and reference your
          designs.
        </p>
        {archId && (
          <Link
            to={`/architects/${archId}`}
            className="wn-btn wn-btn-outline mt-4"
            data-testid="view-public-profile-btn"
          >
            View your public profile <ChevronRight size={16} />
          </Link>
        )}
      </div>
      <div className="mt-7">
        <button
          className="wn-btn wn-btn-primary"
          onClick={() => nav("/architect")}
          data-testid="go-to-portal-btn"
        >
          Go to your Architect Portal <ChevronRight size={16} />
        </button>
      </div>
      <SubmissionSummary app={app} />
    </div>
  );
}

function RejectedPanel({ app }) {
  const nav = useNavigate();
  return (
    <div data-testid="status-rejected">
      <PageHeader
        eyebrow="Application status"
        title="Your application was not approved."
        accentColor="#A23B2E"
      />
      <div
        className="wn-card mt-2"
        style={{
          padding: "16px 18px",
          borderLeft: "3px solid #A23B2E",
          backgroundColor: "rgba(162,59,46,0.06)",
        }}
        data-testid="rejected-note"
      >
        <div className="wn-label" style={{ color: "#A23B2E" }}>
          Review notes
        </div>
        <p
          style={{
            marginTop: 6,
            color: "#272636",
            fontSize: 14,
            lineHeight: 1.6,
          }}
        >
          {app.review_notes || "No additional notes were provided."}
        </p>
      </div>
      <p
        className="mt-5 max-w-[560px]"
        style={{ color: "#6C6B85", fontSize: 14, lineHeight: 1.6 }}
      >
        If your circumstances change, you can submit a new application. This
        rejected application is preserved in your history for reference.
      </p>
      <button
        className="wn-btn wn-btn-outline mt-6"
        onClick={() => nav("/register/architect")}
        data-testid="start-new-application-btn"
      >
        Start a new application <ChevronRight size={16} />
      </button>
      <SubmissionSummary app={app} />
    </div>
  );
}

// ---------------------------------------------------------------------------
function SubmissionSummary({ app }) {
  const docs = [
    app.documents?.license,
    app.documents?.eo,
    ...(app.documents?.portfolio || []),
  ].filter(Boolean);
  return (
    <div className="mt-10" data-testid="application-summary">
      <div
        style={{
          borderTop: "1px solid #D9D8E4",
          marginBottom: 20,
        }}
      />
      <div className="grid md:grid-cols-2 gap-10">
        <div>
          <div className="wn-label">Applicant</div>
          <div
            className="mt-1"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 22,
              color: "#1B1F4B",
            }}
          >
            {app.contact?.name}
          </div>
          <div
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 14,
            }}
          >
            {app.firm}
          </div>
          <div className="mt-2 tabular-nums" style={{ fontSize: 13, color: "#3A5563", fontWeight: 600 }}>
            License · {app.license_no}
          </div>
          <div className="mt-1" style={{ fontSize: 13, color: "#6C6B85" }}>
            {app.contact?.email}
            {app.contact?.phone ? ` · ${app.contact.phone}` : ""}
          </div>
          {app.service_areas?.length > 0 && (
            <div className="mt-4">
              <div className="wn-label">Service areas</div>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {app.service_areas.map((s) => (
                  <Chip key={s}>{s}</Chip>
                ))}
              </div>
            </div>
          )}
          {app.specialisations?.length > 0 && (
            <div className="mt-4">
              <div className="wn-label">Typology specialisations</div>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {app.specialisations.map((s) => (
                  <Chip key={s}>{s}</Chip>
                ))}
              </div>
            </div>
          )}
        </div>
        <div>
          <div className="wn-label">Documents</div>
          <div className="mt-2 space-y-2" data-testid="application-documents">
            {docs.length === 0 && (
              <div style={{ color: "#6C6B85", fontSize: 13 }}>—</div>
            )}
            {docs.map((d, i) => (
              <UploadedRow
                key={d.id || i}
                file={d}
                testidPrefix={`summary-doc-${i}`}
              />
            ))}
          </div>
          {app.submitted_at && (
            <div
              className="mt-5"
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                color: "#6C6B85",
                fontSize: 13,
              }}
              data-testid="submitted-at"
            >
              Submitted {fmtDate(app.submitted_at)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Chip({ children }) {
  return (
    <span
      style={{
        padding: "3px 10px",
        borderRadius: 999,
        backgroundColor: "rgba(91,97,150,0.10)",
        color: "#1B1F4B",
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.04em",
      }}
    >
      {children}
    </span>
  );
}

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}
